export default class BranchValidator {
    static TRANSACTION_BRANCH_KEY = 'pos_transaction_branch';
    static TRANSACTION_TYPE_KEY = 'pos_transaction_type';
    
    /**
     * Store the current branch information when a transaction starts
     * @param {number} branchId - The branch ID where transaction is initiated
     * @param {string} branchName - The branch name where transaction is initiated
     * @param {string} transactionType - Type of transaction (POS, PUR, etc.)
     * @param {string} pharmacyName - The pharmacy name where transaction is initiated
     */
    static storeTransactionBranch(branchId, branchName, transactionType, pharmacyName = '') {
        const transactionData = {
            branchId: branchId,
            branchName: branchName,
            transactionType: transactionType,
            pharmacyName: pharmacyName,
            timestamp: new Date().getTime()
        };
        
        localStorage.setItem(this.TRANSACTION_BRANCH_KEY, JSON.stringify(transactionData));
        localStorage.setItem(this.TRANSACTION_TYPE_KEY, transactionType);
    }
    
    /**
     * Get the stored transaction branch information
     * @returns {Object|null} The stored transaction data or null if not found
     */
    static getStoredTransactionBranch() {
        const stored = localStorage.getItem(this.TRANSACTION_BRANCH_KEY);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.error('Error parsing stored branch data:', e);
                this.clearStoredTransactionBranch();
            }
        }
        return null;
    }
    
    /**
     * Get the stored transaction type
     * @returns {string|null} The transaction type or null if not found
     */
    static getStoredTransactionType() {
        return localStorage.getItem(this.TRANSACTION_TYPE_KEY);
    }
    
    /**
     * Clear the stored transaction branch information
     */
    static clearStoredTransactionBranch() {
        localStorage.removeItem(this.TRANSACTION_BRANCH_KEY);
        localStorage.removeItem(this.TRANSACTION_TYPE_KEY);
    }
    
    /**
     * Check if the current branch matches the stored transaction branch
     * @param {number} currentBranchId - The current user branch ID
     * @param {string} currentBranchName - The current user branch name
     * @returns {Object} Validation result with isValid flag and message
     */
    static validateCurrentBranch(currentBranchId, currentBranchName) {
        const storedData = this.getStoredTransactionBranch();
        
        if (!storedData) {
            return {
                isValid: true,
                message: '',
                storedBranch: null
            };
        }
        
        // Check if branch has changed
        if (storedData.branchId !== currentBranchId) {
            return {
                isValid: false,
                message: `You need to switch to "${storedData.branchName}" branch to proceed with this ${storedData.transactionType} transaction.`,
                storedBranch: storedData
            };
        }
        
        return {
            isValid: true,
            message: '',
            storedBranch: storedData
        };
    }
    
    /**
     * Check if there's an active transaction in progress
     * @returns {boolean} True if there's an active transaction
     */
    static hasActiveTransaction() {
        return this.getStoredTransactionBranch() !== null;
    }
    
    /**
     * Get the age of the stored transaction in minutes
     * @returns {number} Age in minutes, or null if no transaction
     */
    static getTransactionAge() {
        const storedData = this.getStoredTransactionBranch();
        if (!storedData) {
            return null;
        }
        
        const now = new Date().getTime();
        const diff = now - storedData.timestamp;
        return Math.floor(diff / (1000 * 60)); // Convert to minutes
    }
    
    /**
     * Clear old transactions (older than 30 minutes)
     */
    static clearOldTransactions() {
        const age = this.getTransactionAge();
        if (age && age > 30) {
            this.clearStoredTransactionBranch();
        }
    }
}
